'use client';

import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import {
  ClinicalTaskCategoryChip,
  ClinicalTaskStatusToggle,
} from '@heidi/tasks';
import { useMutateUpdateClinicalTask } from '@/hooks/mutation/useMutateUpdateClinicalTask';
import { GET_CLINICAL_TASKS } from '@heidi/tasks/contracts';
import { cn } from '@/lib/cn';
import type {
  ClinicalTaskResultTask,
  GetClinicalTasksResponse,
} from '@heidi/tasks/contracts';
import { ClinicalTaskStatus } from '@heidi/schema/ml-scribe';
import { useTranslations } from '@heidi/runtime/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, ChevronDown, ExternalLink, SquareCheck } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState, type JSX } from 'react';

const MAX_VISIBLE_TASKS = 5;

interface ClinicalTaskResultBlockProps {
  status: 'creating' | 'completed' | 'failed';
  tasks: ClinicalTaskResultTask[];
  error?: string;
  className?: string;
}

/**
 * ClinicalTaskResultBlock - Displays clinical task creation results in the chat timeline
 */
export const ClinicalTaskResultBlock = ({
  status,
  tasks,
  error,
  className,
}: ClinicalTaskResultBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { mutate: updateTask } = useMutateUpdateClinicalTask();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [optimisticStatuses, setOptimisticStatuses] = useState<
    Record<string, ClinicalTaskStatus>
  >({});

  // Build a map of task IDs to their current status from TanStack Query cache
  // This ensures we show the latest status even if updated from master /tasks page
  const cachedTaskStatuses = useMemo(() => {
    const statusMap: Record<string, ClinicalTaskStatus> = {};
    const taskIds = new Set(tasks.map((t) => t.id));

    // Search through all cached clinical task queries
    const queries = queryClient.getQueriesData<GetClinicalTasksResponse>({
      queryKey: [GET_CLINICAL_TASKS],
    });

    for (const [, data] of queries) {
      if (data?.tasks) {
        for (const cachedTask of data.tasks) {
          if (taskIds.has(cachedTask.id)) {
            statusMap[cachedTask.id] = cachedTask.status;
          }
        }
      }
    }

    return statusMap;
  }, [queryClient, tasks]);

  const handleToggleStatus = useCallback(
    (taskId: string, currentStatus: ClinicalTaskStatus) => {
      const newStatus =
        currentStatus === ClinicalTaskStatus.done
          ? ClinicalTaskStatus.todo
          : ClinicalTaskStatus.done;
      setOptimisticStatuses((prev) => ({ ...prev, [taskId]: newStatus }));
      updateTask({ taskId, taskData: { status: newStatus } });
    },
    [updateTask]
  );

  const handleViewAll = useCallback(() => void router.push('/tasks'), [router]);
  const handleToggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  const getTaskStatus = useCallback(
    (task: ClinicalTaskResultTask): ClinicalTaskStatus => {
      // Priority: 1) Local optimistic state, 2) Cached query data, 3) Original metadata
      const optimistic = optimisticStatuses[task.id];
      if (optimistic !== undefined) return optimistic;

      const cached = cachedTaskStatuses[task.id];
      if (cached !== undefined) return cached;

      const map: Record<string, ClinicalTaskStatus> = {
        todo: ClinicalTaskStatus.todo,
        done: ClinicalTaskStatus.done,
        archived: ClinicalTaskStatus.archived,
      };
      return map[task.status] ?? ClinicalTaskStatus.todo;
    },
    [optimisticStatuses, cachedTaskStatuses]
  );

  const completedCount = useMemo(
    () => tasks.filter((task) => getTaskStatus(task) === ClinicalTaskStatus.done).length,
    [tasks, getTaskStatus]
  );

  // Creating state with animated loader
  if (status === 'creating') {
    return (
      <div
        className={cn('flex w-full items-center gap-3 p-3 animate-in fade-in', className)}
      >
        <style>{`
          @keyframes task-shimmer { 0% { mask-position: 150%; } 100% { mask-position: -50%; } }
          @keyframes task-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
        <div className="relative flex size-8 items-center justify-center">
          <svg
            className="absolute inset-0"
            style={{ animation: 'task-spin 2s linear infinite' }}
            width="32"
            height="32"
            viewBox="0 0 34 34"
            fill="none"
          >
            <defs>
              <linearGradient id="tg" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(34 197 94 / 0.1)" />
                <stop offset="50%" stopColor="rgb(34 197 94 / 0.6)" />
                <stop offset="100%" stopColor="rgb(34 197 94 / 0.1)" />
              </linearGradient>
            </defs>
            <circle
              cx="17"
              cy="17"
              r="14"
              stroke="url(#tg)"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
              strokeDasharray="20 60"
            />
          </svg>
          <SquareCheck className="size-4 text-validation-success" />
        </div>
        <TypographyP2
          className="font-medium text-text-secondary"
          style={{
            maskImage:
              'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
            maskSize: '200% 100%',
            WebkitMaskImage:
              'linear-gradient(90deg, #000 0%, #000 35%, rgba(0,0,0,0.4) 50%, #000 65%, #000 100%)',
            WebkitMaskSize: '200% 100%',
            animation: 'task-shimmer 2s ease-in-out infinite',
          }}
        >
          {t('chat.creatingTasks')}
        </TypographyP2>
      </div>
    );
  }

  // Failed state
  if (status === 'failed') {
    return (
      <div
        className={cn('flex w-full items-center gap-3 p-3 animate-in fade-in', className)}
      >
        <FramedIcon size="sm" state="error" icon={<AlertCircle />} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <TypographyP2 className="font-medium text-text-primary">
            {t('chat.tasksCreationFailed')}
          </TypographyP2>
          {error && (
            <TypographyCaption className="text-text-tertiary">{error}</TypographyCaption>
          )}
        </div>
      </div>
    );
  }

  // Completed state
  return (
    <div className={cn('flex w-full flex-col gap-3 animate-in fade-in', className)}>
      {/* Header - no background */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FramedIcon size="sm" state="success" icon={<SquareCheck />} />
          <div className="flex flex-col">
            <span className="font-exposure text-sm font-medium tracking-tight text-text-primary">
              {t('chat.tasksCreated', { count: tasks.length })}
            </span>
            {tasks.length > 1 && (
              <TypographyCaption className="text-text-tertiary">
                {completedCount}/{tasks.length} {t('chat.completed')}
              </TypographyCaption>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewAll}
            className="gap-1 text-text-secondary hover:text-text-primary"
          >
            <ExternalLink className="size-3.5" />
            {t('chat.viewAll')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={handleToggleCollapse}
            className="text-text-secondary hover:text-text-primary"
          >
            <ChevronDown
              className={cn(
                'size-4 transition-transform duration-200',
                isCollapsed && '-rotate-90'
              )}
            />
          </Button>
        </div>
      </div>

      {/* Task list with individual item backgrounds */}
      {!isCollapsed && (
        <>
          {tasks.length > MAX_VISIBLE_TASKS ? (
            <ScrollArea className="max-h-[300px]">
              <div className="flex flex-col gap-2">
                {tasks.map((task) => renderTask(task))}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex flex-col gap-2">
              {tasks.map((task) => renderTask(task))}
            </div>
          )}
        </>
      )}
    </div>
  );

  function renderTask(task: ClinicalTaskResultTask) {
    const taskStatus = getTaskStatus(task);
    return (
      <div
        key={task.id}
        className={cn(
          'flex items-start gap-2 rounded-lg border border-border bg-surface p-2.5',
          taskStatus === ClinicalTaskStatus.done && 'opacity-60'
        )}
      >
        <div className="mt-0.5">
          <ClinicalTaskStatusToggle
            status={taskStatus}
            taskId={task.id}
            onToggle={handleToggleStatus}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2
            className={cn(
              'truncate font-medium',
              taskStatus === ClinicalTaskStatus.done
                ? 'text-text-secondary line-through'
                : 'text-text-primary'
            )}
          >
            {task.title}
          </TypographyP2>
          <ClinicalTaskCategoryChip category={task.category} className="-ms-2" />
        </div>
        {taskStatus === ClinicalTaskStatus.done && (
          <Check className="mt-0.5 size-4 shrink-0 text-validation-success" />
        )}
      </div>
    );
  }
};
